import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { ReserveHeader } from "@/components/ReserveHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { previewNearbyForShift } from "@/lib/admin-nearby-preview.functions";
import { SHIFT_ROLES } from "@/lib/pay-rates";

export const Route = createFileRoute("/_authenticated/admin/nearby-preview")({
  head: () => ({ meta: [{ title: "Nearby Preview — Admin" }] }),
  component: NearbyPreviewPage,
});

type Match = {
  user_id: string;
  full_name: string | null;
  email: string | null;
  postcode: string | null;
  travel_radius_km: number | null;
  distance_km: number;
};
type PreviewResult = {
  ok: true;
  location: { latitude: number; longitude: number };
  matches: Match[];
  preview: { subject: string; html: string; text: string; title: string; body: string };
  sent: { to: string; message_id: string } | null;
};

function NearbyPreviewPage() {
  const run = useServerFn(previewNearbyForShift);
  const [postcode, setPostcode] = useState("");
  const [roleType, setRoleType] = useState<(typeof SHIFT_ROLES)[number]>("bartender");
  const [address, setAddress] = useState("");
  const [startTime, setStartTime] = useState("");
  const [testEmail, setTestEmail] = useState("joeclarke54321@gmail.com");
  const [sendTest, setSendTest] = useState(true);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<PreviewResult | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setResult(null);
    try {
      const r = await run({
        data: {
          postcode,
          role_type: roleType,
          address: address || undefined,
          start_time: startTime ? new Date(startTime).toISOString() : undefined,
          send_test_to: sendTest && testEmail ? testEmail : null,
        },
      });
      setResult(r as PreviewResult);
      if (r.sent) toast.success(`Test invite queued to ${r.sent.to}`);
      else toast.success(`Found ${r.matches.length} bartender(s) in range`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not run preview.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <ReserveHeader role="admin" />
      <main className="mx-auto max-w-5xl px-6 py-10">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <p className="tracking-wide-luxury text-accent">Admin · Notifications</p>
            <h1 className="mt-3 font-display text-3xl">Nearby preview & test invite</h1>
            <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
              Enter a shift location to see exactly which bartenders would be alerted, preview the
              email content they would receive, and optionally send a real test invite.
            </p>
          </div>
          <Link
            to="/admin"
            className="border border-border px-4 py-2 text-xs tracking-wide-luxury hover:border-accent"
          >
            ← Back to console
          </Link>
        </div>
        <span className="gold-divider mt-4" />

        <form
          onSubmit={handleSubmit}
          className="mt-8 grid gap-5 border border-border bg-card p-6 sm:grid-cols-2"
        >
          <div>
            <Label htmlFor="postcode">Shift postcode</Label>
            <Input
              id="postcode"
              required
              value={postcode}
              onChange={(e) => setPostcode(e.target.value)}
              placeholder="e.g. SW1A 1AA"
              autoComplete="off"
              className="mt-2 uppercase"
            />
          </div>
          <div>
            <Label htmlFor="role">Role</Label>
            <select
              id="role"
              value={roleType}
              onChange={(e) => setRoleType(e.target.value as (typeof SHIFT_ROLES)[number])}
              className="mt-2 w-full border border-input bg-background px-3 py-2 text-sm"
            >
              {SHIFT_ROLES.map((r) => (
                <option key={r} value={r}>
                  {r.replace(/_/g, " ")}
                </option>
              ))}
            </select>
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor="address">Address (shown in email)</Label>
            <Input
              id="address"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="Optional — defaults to a sample address"
              className="mt-2"
            />
          </div>
          <div>
            <Label htmlFor="start">Start time</Label>
            <Input
              id="start"
              type="datetime-local"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              className="mt-2"
            />
            <p className="mt-1 text-xs text-muted-foreground">Defaults to ~24h from now.</p>
          </div>
          <div>
            <Label htmlFor="testEmail">Send test invite to</Label>
            <Input
              id="testEmail"
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              className="mt-2"
            />
            <label className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
              <input
                type="checkbox"
                checked={sendTest}
                onChange={(e) => setSendTest(e.target.checked)}
              />
              Actually send (uncheck to preview only)
            </label>
          </div>
          <div className="sm:col-span-2 flex justify-end">
            <Button
              type="submit"
              disabled={loading}
              className="rounded-none bg-foreground text-background hover:bg-foreground/90 tracking-wide-luxury"
            >
              {loading ? "Running…" : "Preview & send"}
            </Button>
          </div>
        </form>

        {result && (
          <div className="mt-10 grid gap-8 lg:grid-cols-2">
            <section>
              <h2 className="font-display text-2xl">
                Would notify {result.matches.length}{" "}
                {result.matches.length === 1 ? "bartender" : "bartenders"}
              </h2>
              <p className="mt-1 text-xs text-muted-foreground">
                Location: {result.location.latitude.toFixed(4)},{" "}
                {result.location.longitude.toFixed(4)}
              </p>
              {result.sent && (
                <p className="mt-3 border border-accent/40 bg-accent/5 px-3 py-2 text-xs">
                  Test invite queued → <strong>{result.sent.to}</strong>
                  <br />
                  Message ID: <code className="text-[11px]">{result.sent.message_id}</code>
                </p>
              )}
              {result.matches.length === 0 ? (
                <p className="mt-6 text-sm text-muted-foreground">
                  No approved bartenders have a travel radius covering this location.
                </p>
              ) : (
                <div className="mt-4 overflow-x-auto border border-border bg-card">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/30 text-left text-xs tracking-wide-luxury text-muted-foreground">
                      <tr>
                        <th className="px-3 py-2">Name</th>
                        <th className="px-3 py-2">Email</th>
                        <th className="px-3 py-2">Home</th>
                        <th className="px-3 py-2">Radius</th>
                        <th className="px-3 py-2">Distance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.matches.map((m) => (
                        <tr key={m.user_id} className="border-t border-border">
                          <td className="px-3 py-2">{m.full_name || "—"}</td>
                          <td className="px-3 py-2 text-muted-foreground">{m.email || "—"}</td>
                          <td className="px-3 py-2 text-muted-foreground">{m.postcode || "—"}</td>
                          <td className="px-3 py-2">{m.travel_radius_km ?? "—"} km</td>
                          <td className="px-3 py-2 text-accent">{m.distance_km.toFixed(1)} km</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            <section>
              <h2 className="font-display text-2xl">Email preview</h2>
              <p className="mt-1 text-xs text-muted-foreground">
                Subject: <strong>{result.preview.subject}</strong>
              </p>
              <div className="mt-3 border border-border bg-white">
                <iframe
                  title="Email preview"
                  srcDoc={result.preview.html}
                  className="h-[520px] w-full"
                />
              </div>
              <details className="mt-3 text-xs">
                <summary className="cursor-pointer text-muted-foreground">Plain text version</summary>
                <pre className="mt-2 whitespace-pre-wrap border border-border bg-muted/20 p-3 text-[11px]">
                  {result.preview.text}
                </pre>
              </details>
            </section>
          </div>
        )}
      </main>
    </div>
  );
}
